import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../configs/firebase';
import { AuthData } from '../provider/AuthProvider';
import { 
  FaBuilding, 
  FaMapMarkerAlt, 
  FaUsers, 
  FaGlobe, 
  FaLinkedin,
  FaCalendar,
  FaIndustry,
  FaArrowLeft,
  FaEye,
  FaSpinner,
  FaClock,
  FaDollarSign,
  FaUserTie, 
  FaTwitter,
  FaFacebook, 
  FaInstagram, 
  FaCheck,
  FaExternalLinkAlt
} from 'react-icons/fa';

const PublicCompanyProfile = () => {
  const { user } = AuthData();
  const { companyId } = useParams();
  const [company, setCompany] = useState(null);
  const [companyPositions, setCompanyPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchCompanyData();
  }, [companyId]);

  const fetchCompanyData = async () => {
    try {
      setLoading(true);
      
      // FIRST: Check if employer exists and is active (from employers collection)
      const employerDoc = await getDoc(doc(db, 'employers', companyId));
      
      if (!employerDoc.exists()) {
        setError('Company not found');
        return;
      }

      const employerData = employerDoc.data();
      
      // Check if company is active/verified
      if (!employerData.isActive || employerData.verificationStatus !== 'approved') {
        setError('This company profile is not available');
        return;
      }

      // SECOND: Load rich marketing profile from companyProfiles collection
      const profileDoc = await getDoc(doc(db, 'companyProfiles', companyId));
      
      let companyData;
      
      if (profileDoc.exists()) {
        // Rich profile exists - use marketing data
        const profileData = profileDoc.data();
        
        companyData = {
          id: companyId,
          // Basic info from rich profile (synced with employers)
          companyName: profileData.basic?.companyName || employerData.companyName,
          description: profileData.basic?.description || employerData.description,
          industry: profileData.basic?.industry || employerData.industry,
          companySize: profileData.basic?.companySize || employerData.companySize,
          foundedYear: profileData.basic?.founded || employerData.foundedYear,
          website: profileData.basic?.website || employerData.website,
          phone: profileData.basic?.phone || employerData.phone,
          email: profileData.basic?.email || employerData.email,
          
          // Enhanced marketing data
          branding: profileData.branding || {},
          team: profileData.team || [],
          culture: profileData.culture || {},
          
          // Social media links
          linkedinUrl: profileData.basic?.linkedinUrl || employerData.linkedinProfile,
          twitterUrl: profileData.basic?.twitterUrl || '',
          facebookUrl: profileData.basic?.facebookUrl || '',
          instagramUrl: profileData.basic?.instagramUrl || '',
          
          // Address handling
          address: {
            street: employerData.streetAddress || '',
            city: employerData.city || '',
            state: employerData.state || '',
            postalCode: employerData.postalCode || '',
            country: employerData.country || ''
          },
          
          // Headquarters from rich profile or constructed from address
          headquarters: profileData.basic?.headquarters || [
            employerData.streetAddress,
            employerData.city,
            employerData.state,
            employerData.postalCode,
            employerData.country
          ].filter(Boolean).join(', '),
          
          // Verification status
          isActive: employerData.isActive,
          verificationStatus: employerData.verificationStatus,
          
          // Rich profile indicators
          hasRichProfile: true,
          profileCompleteness: calculateProfileCompleteness(profileData)
        };
        
        // Load team members if they exist
        if (profileData.team && profileData.team.length > 0) {
          companyData.team = profileData.team;
        } else {
          // Fallback: load from teamMembers collection
          const teamQuery = query(
            collection(db, 'teamMembers'),
            where('employerId', '==', companyId)
          );
          const teamSnapshot = await getDocs(teamQuery);
          companyData.team = teamSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        }
        
      } else {
        // No rich profile - use basic employer data
        companyData = {
          id: companyId,
          companyName: employerData.companyName,
          description: employerData.description || 'No description available.',
          industry: employerData.industry,
          companySize: employerData.companySize,
          foundedYear: employerData.foundedYear,
          website: employerData.website,
          phone: employerData.phone,
          email: employerData.email,
          linkedinUrl: employerData.linkedinProfile,
          
          address: {
            street: employerData.streetAddress || '',
            city: employerData.city || '',
            state: employerData.state || '',
            postalCode: employerData.postalCode || '',
            country: employerData.country || ''
          },
          
          // Default empty rich profile data
          branding: {},
          team: [],
          culture: {},
          
          // Status
          isActive: employerData.isActive,
          verificationStatus: employerData.verificationStatus,
          hasRichProfile: false,
          profileCompleteness: 25 // Basic profile only
        };
      }
      
      setCompany(companyData);
    } catch (error) {
      console.error('Error fetching company data:', error);
      setError('Failed to load company profile');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to calculate profile completeness
  const calculateProfileCompleteness = (profileData) => {
    let score = 0;
    const maxScore = 100;
    
    // Basic info (40 points)
    if (profileData.basic?.companyName) score += 10;
    if (profileData.basic?.description) score += 10;
    if (profileData.basic?.industry) score += 5;
    if (profileData.basic?.website) score += 5;
    if (profileData.basic?.headquarters) score += 10;
    
    // Branding (25 points)
    if (profileData.branding?.logo) score += 10;
    if (profileData.branding?.tagline) score += 5;
    if (profileData.branding?.mission) score += 5;
    if (profileData.branding?.values?.length > 0) score += 5;
    
    // Team (20 points)
    if (profileData.team?.length > 0) score += 20;
    
    // Culture (15 points)
    if (profileData.culture?.workEnvironment) score += 5;
    if (profileData.culture?.benefits?.length > 0) score += 5;
    if (profileData.culture?.learningOpportunities) score += 5;
    
    return Math.min(score, maxScore);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading company profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FaBuilding className="text-4xl text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{error}</h2>
          <Link 
            to="/explore" 
            className="text-orange-600 hover:text-orange-700 font-medium"
          >
            ← Back to Explore
          </Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FaBuilding },
    { id: 'culture', label: 'Culture & Values', icon: FaCheck }
  ];

  // Enhanced Company Header with Branding
  const CompanyHeader = ({ company }) => (
    <div className="relative">
      {/* Cover Image */}
      {company.branding?.coverImage && (
        <div className="h-48 md:h-64 bg-gradient-to-r from-orange-400 to-orange-600 relative overflow-hidden">
          <img 
            src={company.branding.coverImage} 
            alt={`${company.companyName} cover`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
        </div>
      )}
      
      {/* Company Info Section */}
      <div className={`${company.branding?.coverImage ? 'relative -mt-16' : 'pt-8'} pb-6`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:space-x-6">
            {/* Logo */}
            <div className="flex-shrink-0 mb-4 md:mb-0">
              {company.branding?.logo ? (
                <img 
                  src={company.branding.logo} 
                  alt={`${company.companyName} logo`}
                  className="w-24 h-24 md:w-32 md:h-32 rounded-xl border-4 border-white shadow-lg bg-white object-cover"
                />
              ) : (
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-xl border-4 border-white shadow-lg bg-white flex items-center justify-center">
                  <FaBuilding className="text-gray-400 text-3xl md:text-4xl" />
                </div>
              )}
            </div>
            
            {/* Company Name and Tagline */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2">
                {company.companyName}
              </h1>
              {company.branding?.tagline && (
                <p className="text-lg text-gray-600 mb-2">{company.branding.tagline}</p>
              )}
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                {company.industry && (
                  <span className="flex items-center">
                    <FaIndustry className="mr-1" />
                    {company.industry}
                  </span>
                )}
                {company.companySize && (
                  <span className="flex items-center">
                    <FaUsers className="mr-1" />
                    {company.companySize}
                  </span>
                )}
                {company.foundedYear && (
                  <span className="flex items-center">
                    <FaCalendar className="mr-1" />
                    Founded {company.foundedYear}
                  </span>
                )}
              </div>
            </div>
            
            {/* Profile Completeness Badge */}
            {company.hasRichProfile && (
              <div className="flex-shrink-0 mt-4 md:mt-0">
                <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                  ✓ Complete Profile ({company.profileCompleteness}%)
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Enhanced About Section with Mission/Vision
  const AboutSection = ({ company }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">About {company.companyName}</h3>
      
      {/* Description */}
      <div className="prose max-w-none mb-6">
        <p className="text-gray-700 leading-relaxed">{company.description}</p>
      </div>
      
      {/* Mission & Vision */}
      {(company.branding?.mission || company.branding?.vision) && (
        <div className="border-t pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {company.branding?.mission && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Our Mission</h4>
                <p className="text-gray-700">{company.branding.mission}</p>
              </div>
            )}
            {company.branding?.vision && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Our Vision</h4>
                <p className="text-gray-700">{company.branding.vision}</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Core Values */}
      {company.branding?.values?.length > 0 && (
        <div className="border-t pt-6">
          <h4 className="font-semibold text-gray-900 mb-3">Our Values</h4>
          <div className="flex flex-wrap gap-2">
            {company.branding.values.map((value, index) => (
              <span 
                key={index}
                className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium"
              >
                {value}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Team Members Section
  const TeamSection = ({ team }) => {
    if (!team || team.length === 0) return null;
    
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Meet Our Team</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {team.map((member, index) => (
            <div key={member.id || index} className="text-center">
              {/* Photo */}
              <div className="mb-4">
                {member.photo ? (
                  <img 
                    src={member.photo} 
                    alt={member.name}
                    className="w-20 h-20 rounded-full mx-auto object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full mx-auto bg-gray-200 flex items-center justify-center">
                    <FaUserTie className="text-gray-400 text-xl" />
                  </div>
                )}
              </div>
              
              {/* Info */}
              <h4 className="font-semibold text-gray-900">{member.name}</h4>
              <p className="text-orange-600 text-sm font-medium">{member.position}</p>
              {member.department && (
                <p className="text-gray-500 text-sm">{member.department}</p>
              )}
              
              {/* Bio */}
              {member.bio && (
                <p className="text-gray-600 text-sm mt-2 line-clamp-3">{member.bio}</p>
              )}
              
              {/* LinkedIn */}
              {member.linkedinUrl && (
                <a 
                  href={member.linkedinUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center mt-2 text-blue-600 hover:text-blue-700"
                >
                  <FaLinkedin className="mr-1" />
                  <span className="text-sm">LinkedIn</span>
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Company Culture Section
  const CultureSection = ({ culture }) => {
    if (!culture || Object.keys(culture).length === 0) return null;
    
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Company Culture</h3>
        
        <div className="space-y-6">
          {/* Work Environment */}
          {culture.workEnvironment && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Work Environment</h4>
              <p className="text-gray-700">{culture.workEnvironment}</p>
            </div>
          )}
          
          {/* Benefits */}
          {culture.benefits?.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Benefits & Perks</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {culture.benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center text-gray-700">
                    <FaCheck className="text-green-500 mr-2 flex-shrink-0" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Additional Perks */}
          {culture.perks?.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Additional Perks</h4>
              <div className="flex flex-wrap gap-2">
                {culture.perks.map((perk, index) => (
                  <span 
                    key={index}
                    className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                  >
                    {perk}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Learning Opportunities */}
          {culture.learningOpportunities && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Learning & Development</h4>
              <p className="text-gray-700">{culture.learningOpportunities}</p>
            </div>
          )}
          
          {/* Career Growth */}
          {culture.careerGrowth && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Career Growth</h4>
              <p className="text-gray-700">{culture.careerGrowth}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Enhanced Social Links Section
  const SocialLinksSection = ({ company }) => {
    const socialLinks = [
      { url: company.website, icon: FaGlobe, label: 'Website' },
      { url: company.linkedinUrl, icon: FaLinkedin, label: 'LinkedIn' },
      { url: company.twitterUrl, icon: FaTwitter, label: 'Twitter' },
      { url: company.facebookUrl, icon: FaFacebook, label: 'Facebook' },
      { url: company.instagramUrl, icon: FaInstagram, label: 'Instagram' }
    ].filter(link => link.url);
    
    if (socialLinks.length === 0) return null;
    
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Connect With Us</h3>
        <div className="flex flex-wrap gap-3">
          {socialLinks.map((link, index) => {
            const Icon = link.icon;
            return (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Icon className="mr-2 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">{link.label}</span>
                <FaExternalLinkAlt className="ml-2 text-xs text-gray-400" />
              </a>
            );
          })}
        </div>
      </div>
    );
  };

  // Main Company Profile Layout Component
  const CompanyProfileLayout = ({ 
    company, 
    companyPositions, 
    activeTab, 
    setActiveTab
  }) => {
    const tabs = [
      { id: 'overview', label: 'Overview' },
      { id: 'team', label: `Team${company.team?.length ? ` (${company.team.length})` : ''}` },
      { id: 'culture', label: 'Culture' }
    ];

    return (
      <div className="min-h-screen bg-gray-50">
        {/* Enhanced Header */}
        <CompanyHeader company={company} />
        
        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 bg-white sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="-mb-px flex space-x-8 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <AboutSection company={company} />
                  {company.team?.length > 0 && (
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Team Preview</h3>
                        <button
                          onClick={() => setActiveTab('team')}
                          className="text-orange-600 hover:text-orange-700 font-medium text-sm"
                        >
                          View All Team Members →
                        </button>
                      </div>
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {company.team.slice(0, 3).map((member, index) => (
                            <div key={member.id || index} className="text-center">
                              {member.photo ? (
                                <img 
                                  src={member.photo} 
                                  alt={member.name}
                                  className="w-16 h-16 rounded-full mx-auto object-cover mb-2"
                                />
                              ) : (
                                <div className="w-16 h-16 rounded-full mx-auto bg-gray-200 flex items-center justify-center mb-2">
                                  <FaUserTie className="text-gray-400" />
                                </div>
                              )}
                              <h4 className="font-medium text-gray-900 text-sm">{member.name}</h4>
                              <p className="text-orange-600 text-xs">{member.position}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'team' && <TeamSection team={company.team} />}
              {activeTab === 'culture' && <CultureSection culture={company.culture} />}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Company Info Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Info</h3>
                
                <div className="space-y-3 text-sm">
                  {company.industry && (
                    <div className="flex items-center">
                      <FaIndustry className="text-gray-400 mr-3" />
                      <div>
                        <span className="text-gray-500">Industry</span>
                        <p className="font-medium">{company.industry}</p>
                      </div>
                    </div>
                  )}
                  
                  {company.companySize && (
                    <div className="flex items-center">
                      <FaUsers className="text-gray-400 mr-3" />
                      <div>
                        <span className="text-gray-500">Company Size</span>
                        <p className="font-medium">{company.companySize}</p>
                      </div>
                    </div>
                  )}
                  
                  {company.foundedYear && (
                    <div className="flex items-center">
                      <FaCalendar className="text-gray-400 mr-3" />
                      <div>
                        <span className="text-gray-500">Founded</span>
                        <p className="font-medium">{company.foundedYear}</p>
                      </div>
                    </div>
                  )}
                  
                  {company.headquarters && (
                    <div className="flex items-start">
                      <FaMapMarkerAlt className="text-gray-400 mr-3 mt-1" />
                      <div>
                        <span className="text-gray-500">Headquarters</span>
                        <p className="font-medium">{company.headquarters}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Social Links */}
              <SocialLinksSection company={company} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <CompanyProfileLayout 
        company={company}
        companyPositions={companyPositions}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
    </>
  );
};

export default PublicCompanyProfile;